import { useEffect, useState } from "react";

import {
  useAccount,
  useContractRead,
  useContractWrite,
  useWaitForTransaction,
} from "wagmi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAmount, selectBuyMode, setAmount } from "../features/trade";
import {
  Chain,
  Chains,
  SpaceBuyABI,
  USDAllowanceABI,
  USDApproveABI,
} from "./data";
import { selectChain } from "../features/chain";
import { selectToken } from "../features/token";
import { parseAmount, specifyGasLimit } from "../utils/util";
import toast from "react-hot-toast";
import { ethers } from "ethers";
import { useTrade } from "./accountContext";
import { getJicAddress, onJicChange } from "../lib/jic";
import {
  sendInjectedContractTransaction,
  waitForInjectedReceipt,
} from "../lib/injectedWallet";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

const BuyButton = () => {
  const buyMode = useAppSelector(selectBuyMode);
  const chain = useAppSelector(selectChain);
  const token = useAppSelector(selectToken);
  const amount = useAppSelector(selectAmount);
  const { address: signerAddress } = useAccount();
  const [jicAddress, setJicAddress] = useState<string | null>(() =>
    getJicAddress()
  );
  const accountAddress = signerAddress || jicAddress;
  const { reFetchBalance } = useTrade();
  const dispatch = useAppDispatch();
  const [injectedPending, setInjectedPending] = useState(false);

  useEffect(
    () =>
      onJicChange((user) => {
        setJicAddress(user?.sub ? user.sub.toLowerCase() : null);
      }),
    []
  );

  const {
    data: approveRes,
    isLoading: approveLoading,
    write: approve,
    reset: resetApprove,
  } = useContractWrite({
    chainId: Chains[chain].chainId,
    address: Chains[chain].wbtcAddr[token].addr as `0x${string}`,
    abi: USDApproveABI,
    functionName: "approve",
    args: [
      Chains[chain].contractAddr,
      parseAmount(amount, Chains[chain].wbtcAddr[token].decimal),
    ],
    gas: BigInt(specifyGasLimit(chain)),
    onError: (error) => {
      const simpleMsg = error.message.split(".")[0];
      toast.error(simpleMsg);
    },
  });
  const { isLoading: watingApprove } = useWaitForTransaction({
    hash: approveRes?.hash,
    onSuccess() {
      buy?.();
      resetApprove();
    },
  });

  const {
    data: buyRes,
    isLoading: buyLoading,
    write: buy,
    reset: resetBuy,
  } = useContractWrite({
    chainId: Chains[chain].chainId,
    address: Chains[chain].contractAddr as `0x${string}`,
    abi: SpaceBuyABI,
    functionName: "Buy",
    args: [
      Chains[chain].wbtcAddr[token].addr,
      parseAmount(amount, Chains[chain].wbtcAddr[token].decimal),
    ],
    gas: BigInt(specifyGasLimit(chain)),
    onError: (error) => {
      const simpleMsg = error.message.split(".")[0];
      toast.error(simpleMsg);
    },
  });
  const { isLoading: waitingBuy } = useWaitForTransaction({
    hash: buyRes?.hash,
    onSuccess() {
      toast.success("Buy succeeded!");
      reFetchBalance();
      dispatch(setAmount(""));
      resetBuy();
    },
    onError() {
      toast.error("Buy failed!");
      reFetchBalance();
      dispatch(setAmount(""));
      resetBuy();
    },
  });
  const { refetch: refetchAllowance } = useContractRead({
    chainId: Chains[chain].chainId,
    address: Chains[chain].wbtcAddr[token].addr as `0x${string}`,
    abi: USDAllowanceABI,
    functionName: "allowance",
    args: [accountAddress || ZERO_ADDRESS, Chains[chain].contractAddr],
    enabled: false,
  });

  const sendInjectedApproveTransaction = async () => {
    const txHash = await sendInjectedContractTransaction({
      chainId: Chains[chain].chainId,
        chainName: Chains[chain].chainName,
        rpcUrl: Chains[chain].provider,
      to: Chains[chain].wbtcAddr[token].addr as `0x${string}`,
      abi: USDApproveABI,
      functionName: "approve",
      args: [
        Chains[chain].contractAddr,
        parseAmount(amount, Chains[chain].wbtcAddr[token].decimal),
      ],
      gas: BigInt(specifyGasLimit(chain)),
    });
    await waitForInjectedReceipt(txHash, {
        rpcUrl: Chains[chain].provider,
      });
  };

  const sendInjectedBuyTransaction = async () => {
    const txHash = await sendInjectedContractTransaction({
      chainId: Chains[chain].chainId,
        chainName: Chains[chain].chainName,
        rpcUrl: Chains[chain].provider,
      to: Chains[chain].contractAddr as `0x${string}`,
      abi: SpaceBuyABI,
      functionName: "Buy",
      args: [
        Chains[chain].wbtcAddr[token].addr,
        parseAmount(amount, Chains[chain].wbtcAddr[token].decimal),
      ],
      gas: BigInt(specifyGasLimit(chain)),
    });
    await waitForInjectedReceipt(txHash, {
        rpcUrl: Chains[chain].provider,
      });
  };

  async function buyToken() {
    if (!accountAddress) {
      toast.error("Please connect wallet first");
      return;
    }
    if (amount === "" || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
      toast.error("Please enter an amount");
      return;
    }

    try {
      if (!signerAddress) setInjectedPending(true);
      const { data } = await refetchAllowance().catch(() => ({
        data: undefined,
      }));
      const needsApproval =
        data === undefined || data === null
          ? true
          : parseFloat(
              ethers.formatUnits(
                data.toString(),
                Chains[chain].wbtcAddr[token].decimal
              )
            ) < Number(amount);

      if (signerAddress) {
        if (needsApproval) {
          approve?.();
        } else {
          buy?.();
        }
        return;
      }

      if (needsApproval) {
        await sendInjectedApproveTransaction();
      }
      await sendInjectedBuyTransaction();
      toast.success("Buy succeeded!");
      reFetchBalance();
      dispatch(setAmount(""));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Buy failed");
    } finally {
      setInjectedPending(false);
    }
  }

  return (
    <>
      <button
        className="p-1 mt-1 rounded-full h-8 md:h-9 text-sm md:text-base text-white w-20 md:w-28"
        style={{
          background: "linear-gradient(120deg,#b7b7b7 36.9%,#363636 88.84%)",
          opacity: buyMode ? "1" : "0.4",
        }}
        disabled={
          !buyMode ||
          buyLoading ||
          waitingBuy ||
          approveLoading ||
          watingApprove ||
          injectedPending
        }
        onClick={() => {
          buyToken();
        }}
      >
        {buyLoading ||
        waitingBuy ||
        approveLoading ||
        watingApprove ||
        injectedPending
          ? "Processing"
          : "BUY"}
      </button>
    </>
  );
};
export default BuyButton;
